// Keyboard routing (§5.3): normalizes key events, looks commands up by lane,
// handles multi-digit fret entry on tab and the focus guard.

import { setFret } from '@simplenotation/core';
import { COMMANDS, runCommand } from '../commands/registry';
import { edit, getState } from '../state/store';
import type { EditorPrefs } from '../state/store';

export const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** Normalize a keyboard event to a combo string like "Mod+Shift+A", "+", "ArrowLeft". */
export function comboOf(e: KeyboardEvent): string {
  const mod = IS_MAC ? e.metaKey : e.ctrlKey;
  const mods: string[] = [];
  if (mod) mods.push('Mod');
  if (e.altKey) mods.push('Alt');
  let key: string;
  let shiftImplied = false;
  if (/^Key[A-Z]$/.test(e.code)) key = e.code.slice(3);
  else if (/^Digit\d$/.test(e.code)) key = e.code.slice(5);
  else if (/^Numpad\d$/.test(e.code)) key = 'Numpad' + e.code.slice(6);
  else if (e.code === 'NumpadAdd' || e.code === 'NumpadSubtract' || e.code === 'NumpadDecimal' || e.code === 'NumpadEnter') key = e.code;
  else if (e.key === ' ' || e.code === 'Space') key = 'Space';
  else if (e.key.length === 1) {
    key = e.key;
    shiftImplied = true;
  } else key = e.key;
  if (e.shiftKey && !shiftImplied) mods.push('Shift');
  return [...mods, key].join('+');
}

type Lane = 'staff' | 'tab';

interface Binding {
  id: string;
  context: 'any' | 'staff' | 'tab';
}

const PRESETS: Record<EditorPrefs['keymap'], Record<string, string[]>> = {
  default: {},
  finale: {
    'duration.1': ['Alt+7', 'Numpad7'],
    'duration.2': ['Alt+6', 'Numpad6'],
    'duration.4': ['Alt+5', 'Numpad5'],
    'duration.8': ['Alt+4', 'Numpad4'],
    'duration.16': ['Alt+3', 'Numpad3'],
    'duration.32': ['Alt+2', 'Numpad2'],
    'duration.64': ['Alt+1', 'Numpad1'],
    'note.dot': ['.', 'NumpadDecimal'],
  },
  guitarpro: {
    'note.longer': ['NumpadSubtract', '-'],
    'note.shorter': ['NumpadAdd', '+', '='],
    'bar.addAfter': ['Insert', 'Mod+Enter'],
    'note.dot': ['.', 'NumpadDecimal'],
    'note.triplet': ['/', 'Mod+3'],
  },
  sibelius: {
    'duration.64': ['Numpad1', 'Alt+1'],
    'duration.32': ['Numpad2', 'Alt+2'],
    'duration.16': ['Numpad3', 'Alt+3'],
    'duration.8': ['Numpad4', 'Alt+4'],
    'duration.4': ['Numpad5', 'Alt+5'],
    'duration.2': ['Numpad6', 'Alt+6'],
    'duration.1': ['Numpad7', 'Alt+7'],
    'note.dot': ['.', 'NumpadDecimal'],
  },
  notebook: {
    'duration.1': ['Alt+1'],
    'duration.2': ['Alt+2'],
    'duration.4': ['Alt+4'],
    'duration.8': ['Alt+8'],
    'duration.16': ['Alt+6'],
  },
};

export const KEYMAP_PRESETS: { id: EditorPrefs['keymap']; label: string }[] = [
  { id: 'default', label: 'Default' },
  { id: 'finale', label: 'Like Finale Simple Entry' },
  { id: 'guitarpro', label: 'Like Guitar Pro' },
  { id: 'sibelius', label: 'Like Sibelius' },
  { id: 'notebook', label: 'Notebook entry' },
];

let cache: { key: string; map: Map<string, Binding[]> } | null = null;

/**
 * Effective shortcuts for each command: registry defaults, then the preset,
 * then custom overrides. A later layer replaces a command's keys and takes its
 * combos away from the other commands of the same lane, so one combo never
 * runs two commands.
 */
export function effectiveShortcuts(prefs: EditorPrefs): Map<string, string[]> {
  const out = new Map<string, string[]>();
  const ctxOf = new Map(COMMANDS.map((c) => [c.id, c.context ?? 'any']));
  for (const c of COMMANDS) if (c.shortcuts) out.set(c.id, [...c.shortcuts]);
  const claim = (id: string, keys: string[]) => {
    const ctx = ctxOf.get(id) ?? 'any';
    for (const [other, ks] of out) {
      const o = ctxOf.get(other) ?? 'any';
      if (other === id || (ctx !== 'any' && o !== 'any' && ctx !== o)) continue;
      const rest = ks.filter((k) => !keys.includes(k));
      if (rest.length !== ks.length) out.set(other, rest);
    }
    out.set(id, [...keys]);
  };
  for (const [id, keys] of Object.entries(PRESETS[prefs.keymap] ?? {})) claim(id, keys);
  for (const [id, keys] of Object.entries(prefs.customKeys ?? {})) claim(id, keys);
  return out;
}

function bindings(prefs: EditorPrefs): Map<string, Binding[]> {
  const key = prefs.keymap + JSON.stringify(prefs.customKeys ?? {});
  if (cache?.key === key) return cache.map;
  const map = new Map<string, Binding[]>();
  const ctxOf = new Map(COMMANDS.map((c) => [c.id, c.context ?? 'any']));
  for (const [id, keys] of effectiveShortcuts(prefs)) {
    for (const k of keys) {
      const list = map.get(k) ?? [];
      list.push({ id, context: ctxOf.get(id) ?? 'any' });
      map.set(k, list);
    }
  }
  cache = { key, map };
  return map;
}

export function commandForCombo(combo: string, lane: Lane, prefs: EditorPrefs): string | null {
  const list = bindings(prefs).get(combo);
  if (!list) return null;
  return (list.find((b) => b.context === lane) ?? list.find((b) => b.context === 'any'))?.id ?? null;
}

/** True if the event target is a text field or other control that owns typing. */
export function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (t as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'range', 'submit', 'color'].includes(type);
  }
  return false;
}

// Multi-digit fret entry: two digits within 600 ms on the same beat/string combine.
let lastDigit: { value: number; time: number; key: string } | null = null;

function fretDigit(d: number): void {
  const s = getState();
  const c = s.ed.cursor;
  const key = `${c.track}:${c.bar}:${c.voice}:${c.beat}:${c.string}`;
  const now = performance.now();
  if (lastDigit && lastDigit.key === key && now - lastDigit.time < 600 && lastDigit.value > 0 && lastDigit.value * 10 + d <= 30) {
    const value = lastDigit.value * 10 + d;
    lastDigit = { value, time: now, key };
    edit((e) => setFret(e, value), { label: `Fret ${value}`, coalesce: `fret:${key}` });
    return;
  }
  lastDigit = { value: d, time: now, key };
  edit((e) => setFret(e, d), { label: `Fret ${d}`, coalesce: `fret:${key}` });
}

/** Global keydown handler for the score editor. Returns true if handled. */
export function handleKey(e: KeyboardEvent): boolean {
  const s = getState();
  if (e.isComposing) return false;
  // Command search: open synchronously, before anything else sees the key.
  const combo = comboOf(e);
  if (s.modal || s.searchOpen) return false;
  // A dropdown (instrument, tempo, zoom…) keeps focus after it is used; undo
  // and redo still belong to the score there, since a <select> has no undo of
  // its own. Text fields keep their own undo.
  const undoRedo = combo === 'Mod+Z' || combo === 'Mod+Y' || combo === 'Mod+Shift+Z';
  if (isTypingTarget(e.target) && !(undoRedo && e.target instanceof HTMLSelectElement)) return false;
  const lane = s.ed.cursor.lane;
  // Tab digits → frets.
  if (lane === 'tab' && /^(Numpad)?\d$/.test(combo) && s.mode === 'edit') {
    fretDigit(Number(combo.slice(-1)));
    return true;
  }
  const id = commandForCombo(combo, lane, s.editorPrefs);
  if (!id) return false;
  return runCommand(id, undefined, { recent: false }) || true;
}
